import L from 'leaflet';
import bed from '../icons/bed.svg?raw';
import type { LatLng, TransportKind } from '../domain/types';
import { fmtTime, money, tripDur } from '../domain/format';
import { nights } from '../domain/item';
import { conflictOf } from '../domain/plan';
import { TRANSPORT_KINDS } from '../domain/transport';
import { emitChange, select, state } from '../state/store';
import { cssv, transportColor } from '../ui/theme';
import { selectFromMap } from '../ui/selection';

let map: L.Map;
let segmentLayer: L.LayerGroup;
let darkTiles: L.LayerGroup;
let lightTiles: L.LayerGroup;
let initialFit = true;
let mobileFitted = false;
// When false, available (not-in-plan) records are hidden from the map so only
// the plan shows — toggled by the eye button in the Segments panel head.
let showPool = true;

/** Toggle map visibility of not-in-plan records; redraws and returns the new state. */
export function togglePool(): boolean {
  showPool = !showPool;
  drawMap();
  return showPool;
}

/** Whether available (not-in-plan) records are currently shown on the map. */
export const isShowPool = (): boolean => showPool;

/** Set pool visibility without redrawing (caller triggers the redraw). Used to
 * default shared links to a plan-only map. */
export function setShowPool(v: boolean): void {
  showPool = v;
}

/** Re-arm the one-shot auto-fit so the next draw fits the map again — called when
 * the workspace content is replaced (shared-link import, workspace switch). */
export function resetInitialFit(): void {
  initialFit = true;
  mobileFitted = false;
}

const SVGNS = 'http://www.w3.org/2000/svg';

/** The overlay-pane <svg>'s <defs> (created on first use), or null if not ready yet. */
function overlayDefs(): SVGDefsElement | null {
  const svg = document.querySelector<SVGSVGElement>('.leaflet-overlay-pane svg');
  if (!svg) return null;
  let defs = svg.querySelector('defs');
  if (!defs) {
    defs = document.createElementNS(SVGNS, 'defs');
    svg.insertBefore(defs, svg.firstChild);
  }
  return defs as SVGDefsElement;
}

/** Remove arrowhead marker defs left over from the previous draw. */
function clearArrowDefs(): void {
  document.querySelectorAll('.leaflet-overlay-pane svg defs marker.tp-arrow').forEach((m) => m.remove());
}

/**
 * Id of an SVG arrowhead marker filled with `color` and outlined in `halo` (so it
 * stays legible where it overlaps a same-coloured line), created on demand and cached
 * in `reg` for this draw. Applied to a segment line via `marker-mid`, so it sits at the
 * line's midpoint, points along the direction of travel, and re-renders on zoom/pan.
 * A fixed user-space size keeps the head readable on both thin dashed and bold planned
 * legs.
 */
function arrowMarker(color: string, halo: string, reg: Map<string, string>): string | null {
  const cached = reg.get(color);
  if (cached) return cached;
  const defs = overlayDefs();
  if (!defs) return null;
  const id = 'tp-arrow-' + reg.size;
  const marker = document.createElementNS(SVGNS, 'marker');
  marker.setAttribute('id', id);
  marker.setAttribute('class', 'tp-arrow');
  marker.setAttribute('viewBox', '0 0 12 12');
  marker.setAttribute('refX', '6'); // centred on the vertex so the head straddles the midpoint
  marker.setAttribute('refY', '6');
  // at least twice the leg width (weight 4) so the direction reads instantly
  marker.setAttribute('markerWidth', '20');
  marker.setAttribute('markerHeight', '20');
  marker.setAttribute('markerUnits', 'userSpaceOnUse');
  marker.setAttribute('orient', 'auto');
  const tip = document.createElementNS(SVGNS, 'path');
  tip.setAttribute('d', 'M1.5,1.5 L11,6 L1.5,10.5 Z');
  // slightly brighter than the leg so the head pops against its own line
  tip.setAttribute('fill', `color-mix(in srgb, ${color} 72%, white)`);
  tip.setAttribute('stroke', halo);
  tip.setAttribute('stroke-width', '1.4');
  tip.setAttribute('stroke-linejoin', 'round');
  marker.appendChild(tip);
  defs.appendChild(marker);
  reg.set(color, id);
  return id;
}

/** Deterministic pseudo-random in [0,1) from a leg id — the leg's "internal
 * random value" that varies its arc radius, stable across sessions. FNV-1a
 * plus a murmur-style finalizer: short sequential ids ("r1", "r2", …) would
 * otherwise cluster and give near-identical arcs. */
function idRand(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Sample a quadratic-bezier arc between two points in the map's projected
 * (pixel) space. The control point sits perpendicular to the chord at its
 * midpoint; the bulge varies per leg (`seed`) and alternates side with the
 * seed's parity, so several legs between the same cities separate visually.
 * An odd sample count guarantees a true middle vertex for the direction
 * arrowhead (marker-mid).
 */
function arcPoints(a: LatLng, b: LatLng, seed: number): LatLng[] {
  const pa = map.project(a);
  const pb = map.project(b);
  const dx = pb.x - pa.x;
  const dy = pb.y - pa.y;
  const dist = Math.hypot(dx, dy) || 1;
  const side = seed < 0.5 ? 1 : -1;
  const bulge = dist * (0.08 + 0.2 * seed) * side;
  // unit perpendicular to the chord
  const px = -dy / dist;
  const py = dx / dist;
  const cx = (pa.x + pb.x) / 2 + px * bulge;
  const cy = (pa.y + pb.y) / 2 + py * bulge;
  const N = 25; // odd point count → sample N>>1 is the arc's apex
  const pts: LatLng[] = [];
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const mt = 1 - t;
    const x = mt * mt * pa.x + 2 * mt * t * cx + t * t * pb.x;
    const y = mt * mt * pa.y + 2 * mt * t * cy + t * t * pb.y;
    const ll = map.unproject(L.point(x, y));
    pts.push([ll.lat, ll.lng]);
  }
  return pts;
}

/** Create the Leaflet map, tile layers, and global map interactions. */
export function initMap(): void {
  map = L.map('map', {
    worldCopyJump: true, minZoom: 2, zoomControl: true,
    // finer zoom granularity: half-step +/- buttons, quarter-step fit snapping,
    // and a slower scroll wheel so an exact scale is easier to reach
    zoomSnap: 0.25, zoomDelta: 0.5, wheelPxPerZoomLevel: 120,
  }).setView([45, 6], 5);
  // CARTO's dark_all/light_all raster basemaps now require a registered API key
  // (they're being retired in favor of vector tiles — see
  // https://github.com/CartoDB/basemap-styles). Anonymous requests silently get
  // served a watermarked "API KEY REQUIRED" placeholder instead of an error, so
  // we switched to Esri's keyless "Gray Canvas" tiles, which look similar.
  // If swapping providers again in the future, verify the new provider's
  // current keyless/free-tier terms first to avoid a silent regression like this.
  const attribution = '© OpenStreetMap contributors · © Esri';
  darkTiles = L.layerGroup([
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19, attribution,
    }),
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19, attribution,
    }),
  ]);
  lightTiles = L.layerGroup([
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19, attribution,
    }),
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19, attribution,
    }),
  ]);
  segmentLayer = L.layerGroup().addTo(map);
  applyTiles();
  map.on('moveend zoomend', () => placeLegend());
  map.on('click', () => {
    if (state.selected !== null) {
      select(null);
      emitChange();
    }
  });
  window.addEventListener('resize', () => {
    map.invalidateSize();
    placeLegend();
  });
}

/** Swap the tile layer to match the active theme. */
export function applyTiles(): void {
  const light = document.body.dataset.theme === 'light';
  map.removeLayer(light ? darkTiles : lightTiles);
  (light ? lightTiles : darkTiles).addTo(map);
}

/** Redraw all segment/hotel geometry from current state. */
export function drawMap(): void {
  segmentLayer.clearLayers();
  clearArrowDefs();
  const bounds: LatLng[] = [];
  const halo = cssv('--halo');
  const accent = cssv('--accent');
  const danger = cssv('--danger');
  // uniform transparent click/hover width so thin dashed legs are as easy to grab as thick ones
  const HITW = 14;
  const usedTransports = new Set<TransportKind>();
  const arrowReg = new Map<string, string>();

  state.items.forEach((r) => {
    // "eye" toggle: hide available (not-in-plan) records, but keep the selected
    // one visible so revealing a card from the pool still works
    const hidden = !showPool && !r.inPlan && state.selected !== r.id;
    if (r.kind === 'hotel') {
      if (!r.ll || hidden) return;
      const isSel = state.selected === r.id;
      const cls = 'hotel-pin' + (r.inPlan ? ' plan' : '') + (isSel ? ' sel' : '');
      const m = L.marker(r.ll, {
        icon: L.divIcon({ html: bed, className: cls, iconSize: [26, 26], iconAnchor: [13, 13] }),
        opacity: r.inPlan ? 1 : 0.65,
        zIndexOffset: r.inPlan ? 600 : 300,
      }).addTo(segmentLayer);
      m.bindTooltip(
        `🏨 ${r.name}<br><small>${r.city} · ${money(r)} · ${nights(r)} night${nights(r) > 1 ? 's' : ''}</small>`,
        { className: 'segment-tip', direction: 'top' },
      );
      m.on('click', () => selectFromMap(r));
      bounds.push(r.ll);
      return;
    }

    if (!r.dep.ll || !r.arr.ll || hidden) return;
    const col = transportColor(r.transport);
    const conflict = conflictOf(state.items, r);
    const isSel = state.selected === r.id;
    if (conflict && !isSel) return; // overlapped: not drawn unless selected

    // planned and unplanned legs share one weight: planned solid, unplanned
    // dashed (a selected unplanned leg KEEPS its dash — selection shows via
    // the accent casing, not by changing the line style)
    const weight = 4;
    let opacity = 1;
    let dash: string | null = null;
    let lineCol = col;
    if (conflict) {
      opacity = 0.9;
      lineCol = danger;
      dash = '6 8';
    } else if (!r.inPlan) {
      opacity = 0.7;
      dash = '6 8';
    }
    const selHi = isSel && !conflict; // selected, non-conflicting → highlight
    const line = arcPoints(r.dep.ll, r.arr.ll, idRand(r.id));

    if (selHi) {
      // selection casing: accent border + neutral separator ring (keeps the border
      // visible even when the leg's own colour IS the accent, e.g. Plane) + soft glow —
      // mirrors the accent border/halo of a selected hotel pin (.hotel-pin.sel)
      L.polyline(line, { color: accent, weight: weight + 12, opacity: 0.28, interactive: false, lineCap: 'round' }).addTo(segmentLayer);
      L.polyline(line, { color: accent, weight: weight + 7, opacity: 1, interactive: false, lineCap: 'round' }).addTo(segmentLayer);
      L.polyline(line, { color: halo, weight: weight + 3, opacity: 1, interactive: false, lineCap: 'round' }).addTo(segmentLayer);
    } else if (r.inPlan) {
      // decorative glow: never captures clicks
      L.polyline(line, { color: halo, weight: weight + 4, opacity: 0.6, interactive: false }).addTo(segmentLayer);
    }
    // visible line is decorative; a fat transparent "hit line" on top carries click + hover
    // smoothFactor:0 keeps every arc sample (Leaflet would otherwise simplify)
    L.polyline(line, { color: lineCol, weight, opacity, dashArray: dash ?? undefined, interactive: false, smoothFactor: 0 }).addTo(segmentLayer);
    if (lineCol === col) usedTransports.add(r.transport); // legend lists only transports whose colour is on the map
    // marker-mid renders on EVERY interior vertex, so the arrowhead rides a
    // separate invisible 3-point carrier at the arc's apex (stroke-opacity 0
    // hides the line; SVG markers paint regardless of stroke opacity)
    const arrowId = arrowMarker(lineCol, halo, arrowReg);
    const apex = line.length >> 1;
    const carrier = L.polyline([line[apex - 1], line[apex], line[apex + 1]], {
      color: lineCol, weight: 1, opacity: 0, interactive: false, smoothFactor: 0,
    }).addTo(segmentLayer);
    const cpath = (carrier as unknown as { getElement(): SVGPathElement | null }).getElement();
    if (cpath && arrowId) cpath.setAttribute('marker-mid', `url(#${arrowId})`);
    const tip = conflict ? `<br><small style="color:${danger}">⚠ overlaps plan</small>` : '';
    // bubblingMouseEvents:false → clicking selects without firing the map's "click empty → unselect"
    const hit = L.polyline(line, { color: '#000', opacity: 0, weight: HITW, lineCap: 'round', bubblingMouseEvents: false }).addTo(segmentLayer);
    hit.bindTooltip(
      `${r.dep.city} → ${r.arr.city}<br><small>${fmtTime(r.dep.time)} · ${r.transport} · ${money(r)} · ⏱ ${tripDur(r)}</small>${tip}`,
      { className: 'segment-tip', sticky: true },
    );
    hit.on('click', () => selectFromMap(r));

    // endpoints contribute to map bounds; only the departure gets a dot — the
    // mid-line arrowhead points toward the arrival so the leg reads directionally
    [r.dep, r.arr].forEach((p) => { if (p.ll) bounds.push(p.ll); });
    if (r.dep.ll) {
      L.circleMarker(r.dep.ll, {
        radius: r.inPlan ? 6 : 4, color: halo, weight: 2,
        fillColor: isSel ? (conflict ? danger : accent) : col, fillOpacity: 1, bubblingMouseEvents: false,
      })
        .bindTooltip(r.dep.city, { className: 'place-tip', direction: 'top' })
        .on('click', () => selectFromMap(r))
        .addTo(segmentLayer);
    }
  });

  if (initialFit && bounds.length) {
    // Fit the same way the Fit button does, so the app opens framed tightly on
    // the itinerary instead of a loose default view.
    initialFit = false;
    fitAll();
  }
  buildLegend(usedTransports);
  placeLegend();
}

/** Place the legend in whichever map corner is least covered by drawn segments. */
export function placeLegend(): void {
  const leg = document.querySelector<HTMLElement>('.map-legend');
  const wrap = document.querySelector<HTMLElement>('.map-wrap');
  if (!leg || !wrap) return;
  const W = wrap.clientWidth;
  const H = wrap.clientHeight;
  const lw = leg.offsetWidth;
  const lh = leg.offsetHeight;
  const m = 14;
  if (!W || !H || !lw) return;
  const corners: Record<string, { left: number; top: number }> = {
    bl: { left: m, top: H - lh - m },
    br: { left: W - lw - m, top: H - lh - m },
    tl: { left: m, top: m },
    tr: { left: W - lw - m, top: m },
  };
  const pts: Array<[number, number]> = [];
  state.items.forEach((r) => {
    if (r.kind === 'hotel') {
      if (!r.ll) return;
      try {
        const p = map.latLngToContainerPoint(r.ll);
        pts.push([p.x, p.y]);
      } catch { /* map not ready */ }
      return;
    }
    if (!r.dep.ll || !r.arr.ll) return;
    const conflict = conflictOf(state.items, r);
    const isSel = state.selected === r.id;
    if (conflict && !isSel) return; // only sample drawn segments
    let a: L.Point;
    let b: L.Point;
    try {
      a = map.latLngToContainerPoint(r.dep.ll);
      b = map.latLngToContainerPoint(r.arr.ll);
    } catch {
      return;
    }
    for (let t = 0; t <= 1.0001; t += 0.06) pts.push([a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t]);
  });
  const cover = (c: { left: number; top: number }): number => {
    const x0 = c.left - 8;
    const y0 = c.top - 8;
    const x1 = c.left + lw + 8;
    const y1 = c.top + lh + 8;
    let n = 0;
    for (const p of pts) if (p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1) n++;
    return n;
  };
  let best = 'bl';
  let bn = Infinity;
  for (const k of ['bl', 'br', 'tl', 'tr']) {
    const n = cover(corners[k]);
    if (n < bn) {
      bn = n;
      best = k;
    }
  }
  const c = corners[best];
  leg.style.left = Math.max(m, c.left) + 'px';
  leg.style.top = Math.max(m, c.top) + 'px';
  leg.style.right = 'auto';
  leg.style.bottom = 'auto';
  leg.dataset.corner = best;
}

/** Build the transport colour legend — only modes whose colour is currently on the map. */
export function buildLegend(used: Set<TransportKind>): void {
  const el = document.getElementById('legTransport');
  if (!el) return;
  const kinds = TRANSPORT_KINDS.filter((t) => used.has(t));
  el.innerHTML = kinds
    .map((t) => `<div class="lrow"><span class="lg-line" style="border-top-color:${transportColor(t)}"></span> ${t}</div>`)
    .join('');
  const col = el.closest('.legcol') as HTMLElement | null;
  if (col) col.style.display = kinds.length ? '' : 'none'; // hide the whole column when nothing is drawn
}

/** Zoom/pan to fit every geocoded record. */
export function fitAll(): void {
  const b: LatLng[] = [];
  state.items.forEach((r) => {
    if (r.kind === 'hotel') {
      if (r.ll) b.push(r.ll);
      return;
    }
    if (r.dep.ll) b.push(r.dep.ll);
    if (r.arr.ll) b.push(r.arr.ll);
  });
  if (!b.length) return;
  // The panel may have been resized/shown since the last draw — Leaflet must
  // re-measure the container or it fits to a stale (smaller) viewport.
  map.invalidateSize();
  map.fitBounds(L.latLngBounds(b), { padding: [20, 20], maxZoom: 13 });
}

/** Recompute map size + legend (after a resize or layout change). */
export function refreshMap(): void {
  map.invalidateSize();
  placeLegend();
}

/** Pan the map to a coordinate (used when revealing a record from a card). */
export function panMapTo(ll: LatLng): void {
  map.panTo(ll, { animate: true });
}

/** Called when the mobile Map tab becomes visible: resize, first-time fit, legend. */
export function onMapTabShown(): void {
  map.invalidateSize();
  if (!mobileFitted) {
    fitAll();
    mobileFitted = true;
  }
  placeLegend();
}
